import { ITopOffersTradePreviewDetails } from 'models/top-offers/i-top-offers';
import { ITradePreviewDetails } from 'models/trade/i-trade-preview-details.interface';

import { useCurrencyWithBalance } from 'hooks/useCurrency';
import { useDecimalPrice } from 'hooks/useDecimalPrice';
import { useEnv } from 'hooks/useEnv';
import { useTraits } from 'hooks/useTraits';

import Button from '../../../buttons/Button';
import ElementLoader from '../../../element-loader/ElementLoader';
import ValueAndIcon from '../../../generics/ValueAndIcon';
import VenueDetailsCardMain from '../../../venueDetailsCard/venueDetailsCardMain/VenueDetailsCardMain';
import CheckoutBalance from '../../checkoutBalance/CheckoutBalance';
import CheckoutProgressSection from '../checkoutProgressSection/CheckoutProgressSection';

interface IPurchaseCheckoutProps {
  confirmationPending: boolean;
  tradePreviewDetails: ITradePreviewDetails | ITopOffersTradePreviewDetails;
  buyOffer: () => void;
}

const PurchaseCheckout = ({ confirmationPending, buyOffer, tradePreviewDetails }: IPurchaseCheckoutProps) => {
  const { royaltiesFee, tradeFee } = useEnv();
  const currency = useCurrencyWithBalance(tradePreviewDetails.tradeDetails.currency_id);
  const { id, category } = useTraits(tradePreviewDetails.assetDetails.traits);
  const price = +tradePreviewDetails.tradeDetails.price;
  const royaltiesFeeAmount = useDecimalPrice(
    (royaltiesFee * price) / 100,
    tradePreviewDetails.tradeDetails.currency_id
  );
  const tradeFeeCalculated = useDecimalPrice((tradeFee * price) / 100, tradePreviewDetails.tradeDetails.currency_id);
  const totalPrice = useDecimalPrice(price, tradePreviewDetails.tradeDetails.currency_id);
  const pricePerShare = useDecimalPrice(
    +tradePreviewDetails.tradeDetails.price / +tradePreviewDetails.tradeDetails.amount,
    tradePreviewDetails.tradeDetails.currency_id
  );

  return (
    <div className="checkout-modal__wrapper">
      <div className="checkout-modal__content">
        <div className="checkout-modal__information-card">
          <VenueDetailsCardMain
            scale={0.75}
            id={+id}
            address={tradePreviewDetails.assetDetails.address}
            availableShares={+tradePreviewDetails.tradeDetails.amount}
            cardUrl={tradePreviewDetails.assetDetails.asset_original_url}
            category={category}
            name={tradePreviewDetails.assetDetails.name}
            iconUrl={tradePreviewDetails.assetDetails.marker_url}
          ></VenueDetailsCardMain>
          <div className="checkout-modal__information-card-details">
            <p className="checkout-modal__caption-name">{tradePreviewDetails.assetDetails.name}</p>
            <p className="checkout-modal__caption-address">{tradePreviewDetails.assetDetails.address}</p>
            <ul className="checkout-modal__payment-list">
              <li className="checkout-modal__payment-item">
                {currency && (
                  <ElementLoader>
                    <CheckoutBalance currency={currency} />
                  </ElementLoader>
                )}
              </li>
              <li className="checkout-modal__payment-item">
                <span>Price per share</span>
                <div className="checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={pricePerShare.toString()}
                  />
                </div>
              </li>
              <li className="checkout-modal__payment-item">
                <span>Royalties fee {royaltiesFee} %</span>
                <div className="checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={royaltiesFeeAmount}
                  />
                </div>
              </li>
              <li className="checkout-modal__payment-item">
                <span>Trade fee {tradeFee} %</span>
                <div className="checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={tradeFeeCalculated}
                  />
                </div>
              </li>
              <li className="checkout-modal__payment-item checkout-modal__payment-summary">
                <span>You will pay total</span>
                <div className="checkout-modal__payment-item-value">
                  <ValueAndIcon valueSize={24} iconSize={24} icon={currency?.icon} value={totalPrice} />
                </div>
              </li>
            </ul>
          </div>
        </div>
        {confirmationPending && <CheckoutProgressSection title="Confirm purchase" />}
        <Button
          type="button"
          color="primary"
          className="checkout-modal__button"
          text={confirmationPending ? 'waiting for approval...' : 'complete purchase'}
          disabled={confirmationPending}
          onClick={buyOffer}
        ></Button>
      </div>
    </div>
  );
};

export default PurchaseCheckout;
